<template>
  <div v-if="file">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div
        class="col-6"
        style="justify-content flex-start"
      >
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="getString('action.toOverview')"
          @click="$router.push('/library')"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn
          style="float: right"
          class="capitalize ctrl-button right"
          :color="edit ? 'ctrl-turquoise' :  'grey-5'"
          :label="edit ? getString('action.save') : getString('action.edit')"
          @click="editDetail()"
          :icon-right="edit ? 'save' : 'edit'"
        />
      </div>
    </div>

    <div
      class="row q-mb-md"
      style="line-height: 1.5rem;"
    >
      <q-icon
        class="q-mr-sm"
        :name="fileIcon"
        color="grey-6"
        size="1.5rem"
      ></q-icon>
      <span class="middle">
        {{ file.folder.name }} /
        <b>{{ file.name }}</b>
      </span>
    </div>

    <div class="row gutter-sm q-mb-lg">
      <!-- Left column -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <!-- Image block -->
        <div class="row q-mb-lg">
          <div class="col file-data">
            <div
              class="card-block file-preview"
              :class="isImage && 'image'"
            >
              <img
                v-if="isImage"
                :src="`${imgSrc}?timestamp=${Date.now()}`"
              />
              <q-icon
                v-else
                :name="fileIcon"
                color="grey-6"
                size="4rem"
              ></q-icon>
            </div>
          </div>
        </div>

        <!-- Details block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">{{ getString('module.library.details.header') }}</p>
            <div class="card-block scroll">
              <!-- file type -->
              <div class="row q-mb-md">
                <div class="col-md-4">
                  <b>{{ getString('module.library.details.file.type') }}</b>
                </div>
                <div class="col-md-8">{{ `.${file.ext}` }}</div>
              </div>

              <!-- file size -->
              <div class="row q-mb-md">
                <div class="col-md-4">
                  <b>{{ getString('module.library.details.file.size') }}</b>
                </div>
                <div class="col-md-8">{{ readableBytes(file.size) }}</div>
              </div>

              <!-- image width -->
              <div
                class="row q-mb-md"
                v-if="file.type === 'image'"
              >
                <div class="col-md-4">
                  <b>{{ getString('module.library.details.file.image.width') }}</b>
                </div>
                <div class="col-md-8">{{ width }}px</div>
              </div>

              <!-- image height -->
              <div
                class="row q-mb-md"
                v-if="file.type === 'image'"
              >
                <div class="col-md-4">
                  <b>{{ getString('module.library.details.file.image.height') }}</b>
                </div>
                <div class="col-md-8">{{ height }}px</div>
              </div>

              <!-- image focuspoint -->
              <div
                class="row q-mb-md"
                v-if="file.type === 'image'"
              >
                <div class="col-md-4">
                  <b>{{ getString('module.library.details.file.image.focuspoint') }}</b>
                </div>
                <div
                  v-if="fileData.focuspoint"
                  class="col-md-8"
                >{{ `x: ${fileData.focuspoint.x}, y: ${fileData.focuspoint.y}` }}</div>
                <div
                  v-else
                  class="col-md-8"
                >-</div>
              </div>

              <!-- last change -->
              <div class="row q-mb-md">
                <div class="col-md-4">
                  <b>{{ getString('module.library.details.file.lastChange') }}</b>
                </div>
                <div class="col-md-8">{{ update }}</div>
              </div>

              <div class="row buttons q-pr-sm q-pt-sm">
                <q-btn
                  v-if="isImage"
                  class="capitalize ctrl-button q-ma-sm"
                  color="ctrl-turquoise"
                  :label="getString('module.library.button.image.edit')"
                  @click="goToEdit()"
                  icon="mdi-content-cut"
                />
                <q-btn
                  class="capitalize ctrl-button q-ma-sm"
                  color="ctrl-turquoise"
                  :label="getString('module.library.button.download')"
                  @click="downloadFile()"
                  icon="mdi-file-download"
                />
                <input
                  type="file"
                  ref="buttonUpload"
                  v-show="false"
                  @change="uploadFiles"
                />
                <q-btn
                  class="capitalize ctrl-button q-ma-sm"
                  color="ctrl-turquoise"
                  @click="$refs.buttonUpload.click()"
                  :label="getString('module.library.button.replace')"
                  icon="mdi-file-replace"
                />
                <q-btn
                  class="capitalize ctrl-button q-ma-sm"
                  color="ctrl-turquoise"
                  @click="restoreOriginal()"
                  :label="getString('module.library.button.restore')"
                  icon="mdi-file-replace"
                />
                <q-btn
                  class="capitalize ctrl-button q-ma-sm"
                  color="ctrl-turquoise"
                  :label="'Get url'"
                  @click="toggleUrlShow()"
                  icon="mdi-link-variant"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-sm-12 col-xs-12">
        <!-- Info block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <div class="card-block scroll">
              <!-- Field: file name -->
              <q-field class="q-mb-md">
                <q-input
                  type="text"
                  v-model="fileData.name"
                  :readonly="!edit"
                  :float-label="getString('module.library.details.file.name')"
                ></q-input>
              </q-field>

              <!-- Field: Title -->
              <q-field class="q-mb-md">
                <q-input
                  type="text"
                  v-model="fileData.title"
                  :readonly="!edit"
                  :float-label="getString('module.library.details.file.title')"
                ></q-input>
              </q-field>

              <!-- Field: Alternative text -->
              <q-field class="q-mb-md">
                <q-input
                  type="text"
                  v-model="fileData.alt"
                  :readonly="!edit"
                  :float-label="getString('module.library.details.file.altText')"
                ></q-input>
              </q-field>

              <!-- Field: Caption -->
              <q-field
                class="q-mb-md"
                v-if="isImage"
              >
                <q-input
                  type="text"
                  v-model="fileData.caption"
                  :readonly="!edit"
                  :float-label="getString('module.library.details.file.caption')"
                ></q-input>
              </q-field>

              <!-- Field: Description -->
              <q-field class="q-mb-md">
                <q-input
                  type="textarea"
                  v-model="fileData.description"
                  :readonly="!edit"
                  :float-label="getString('module.library.details.file.description')"
                ></q-input>
              </q-field>

              <!-- Toggle for no-index -->
              <q-field class="q-mb-md">
                <q-toggle
                  v-model="fileData.no_index"
                  :true-value="1"
                  :false-value="0"
                  label="No index"
                  :disable="!edit"
                />
              </q-field>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-modal v-model="showUrl">
      <div
        class="row"
        style="display: flex; justify-content: flex-end"
      >
        <q-btn
          icon="close"
          @click="showUrl = false"
        />
      </div>
      <div class="url-modal">
        <div class="url-modal__content">
          <p ref="url">{{ fileToSiteLocation() }}</p>
          <q-btn
            class="capitalize ctrl-button q-ma-sm"
            color="ctrl-turquoise"
            :label="'copy url to clipboard'"
            @click="copyToClipboard()"
            icon="mdi-content-copy"
          />
        </div>
      </div>
    </q-modal>
  </div>
</template>

<script>
import moment from 'moment';
import qs from 'qs';

// Helpers
import getString from '@/helpers/GetStrings';

import common from '@/mixins/library/common';

export default {
  mixins: [
    common,
  ],
  data() {
    return {
      showUrl: false,
      edit: false,
      icons: {
        mp3: 'mdi-music-box',
        pdf: 'mdi-file-pdf',
        jpg: 'image',
        jpeg: 'image',
        png: 'image',
        svg: 'image',
        tiff: 'image',
        txt: 'mdi-file-document-box-outline',
        doc: 'mdi-file-word',
        docx: 'mdi-file-word',
        gif: 'gif',
        xls: 'mdi-file-excel',
        zip: 'mdi-zip-box',
        unknown: 'mdi-file-outline',
      },
      dateFormat: {
        'yyyy-MM-dd': 'YYYY-MM-DD',
        'dd-MM-yyyy': 'DD-MM-YYYY',
        'd-M-yyyy': 'D-M-YYYY',
        'dd-MM-yy': 'DD-MM-YY',
        'd-M-yy': 'D-M-YY',
      },
      uploadsQueue: [],
      uploadCount: 0,
      file: null,
      width: null,
      height: null,
      imgSrc: null,
      fileData: {
        name: null,
        title: null,
        alt: null,
        description: null,
        caption: '* Caption text',
        no_index: 0,
      },
    };
  },
  methods: {
    getString,
    copyToClipboard() {
      this.selectText(this.$refs.url);
      document.execCommand('copy');

      // Notify copied to clipboard
      this.$q.notify({
        message: 'Copied url to clipboard',
        timeout: 2000,
        type: 'positive',
        position: 'top-right',
      });
    },
    selectText(element) {
      let range;
      if (document.selection) {
        // IE
        range = document.body.createTextRange();
        range.moveToElementText(element);
        range.select();
      } else if (window.getSelection) {
        range = document.createRange();
        range.selectNode(element);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    },
    toggleUrlShow() {
      this.showUrl = true;
    },
    fileToSiteLocation() {
      const location = this.fileData.folder.location.replace('api/', '');
      return `${process.env.VUE_APP_SITE}${location}id/${this.fileData.id}/${encodeURIComponent(this.fileData.name)}`;
    },
    // The follwing method is fired when files are dropped/selected in the input
    uploadFiles() {
      // Define files from file-input
      const files = this.$refs.buttonUpload.files;
      // Loop over files
      for (let i = 0; i < files.length; i += 1) {
        // For every file call the readFiles method
        this.readFiles(files[i], i);
        this.uploadsQueue[i] = { cd: files[i].name, ct: files[i].type, binary: '' };
      }
    },

    restoreOriginal() {
      // Open dialog
      this.$q.dialog({
        message: getString('module.library.dialog.image.restore'),
        ok: getString('dialog.confirm.ok'),
        cancel: getString('dialog.confirm.cancel'),
        color: 'warning',
      }).then(() => {
        const old = decodeURIComponent(this.$route.params.filename);

        this.axios.put(`library/${old}`,
          qs.stringify({ restore: old }),
          { headers: { 'Content-Disposition': 'inline' } })
          .then(() => {
            // Update the view to fetch the uncached image
            this.$forceUpdate();
            this.qNotify('module.library.file.details.edit.success', 'positive', 'top-right');
          })
          .catch((err) => {
            console.warn(err);
          });
      });
    },

    // Method which creates a Filereader instance to handle files (binary)
    readFiles(file, i) {
      // Define new filereader object
      const reader = new FileReader();
      // Add custum value 'index' for right position in uploadsQueue
      reader.index = i;
      // Define readSuccess method
      reader.onload = this.readSuccess;
      // Call readAsBinaryString method which will call readsucess method as a result
      reader.readAsArrayBuffer(file);
    },

    // Method which is called when reader onload succeeds
    readSuccess(evt) {
      this.uploadsQueue[evt.target.index].binary = evt.target.result;
      // // Upload file
      this.uploadFileQueue(this.uploadsQueue[evt.target.index]);
    },

    async uploadFileQueue(file) {
      const res = await this.axios.put(`/library/${decodeURIComponent(this.$route.params.filename)}`, file.binary, {
        headers: {
          'Content-Disposition': `attachment; filename="${this.fileData.name}"`,
          'Content-Type': file.ct,
        },
      });
      if (res.status === 200) {
        this.$forceUpdate();
      }
    },

    downloadFile() {
      this.axios.get(`/library/download/${decodeURIComponent(this.$route.params.filename)}`, {
        responseType: 'blob',
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.fileData.name);
        document.body.appendChild(link);
        link.click();
      });
    },

    replaceFile() {
      // Replace the current file while keeping the meta data
      this.axios.put(`/library/download/${decodeURIComponent(this.$route.params.filename)}`);
    },

    qNotify(msg, color, position, stringsStore = true, options = {}) {
      this.$q.notify(Object.assign({
        message: stringsStore ? getString(msg) : msg,
        color,
        position,
      }, options));
    },

    async editDetail() {
      if (this.edit) {
        Object.assign(this.fileData, { filename: this.fileData.name });
        const allowed = ['filename', 'title', 'alt', 'caption', 'description', 'no_index'];

        this.fileData.no_index = Number(this.fileData.no_index);

        // Only send the allowed fields in the request
        const filtered = Object.keys(this.fileData)
          .filter(key => allowed.includes(key))
          .filter(key => this.fileData[key] !== null && this.fileData[key] !== '')
          .reduce((obj, key) => {
            obj[key] = this.fileData[key];
            return obj;
          }, {});

        const response = await this.axios.put(`/library/${decodeURIComponent(this.$route.params.filename)}`, qs.stringify(filtered), { headers: { 'Content-Disposition': 'inline' } });
        if (response.status === 200) {
          this.edit = !this.edit;
          this.qNotify('module.library.file.details.edit.success', 'positive', 'top-right');
        } else {
          this.qNotify('module.library.file.details.edit.error', 'negative', 'top-right');
        }
      } else {
        this.edit = !this.edit;
      }
    },

    goToEdit() {
      this.$router.push(`/library/${encodeURIComponent(this.$route.params.filename)}/edit`);
    },
  },
  computed: {
    update() {
      return moment(this.file.update).format(`${this.dateFormat[this.$store.state.meStore.me.settings.date]}`);
    },
    fileIcon() {
      return this.icons[this.file.ext] ? this.icons[this.file.ext] : this.icons.unknown;
    },
  },
  mounted() {
    this.fetchLibraryFile(decodeURIComponent(this.$route.params.filename));
  },
};
</script>

<style lang="stylus">
.url-modal {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 250px;

  &__content {
    padding: 30px;
    justify-content: center;
  }
}

.file-preview {
  display: flex;
  justify-content: center;
  height: 40vh;
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  position: relative;

  img {
    max-width: 100%;
    max-height: 100%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
}

.file-preview.image {
  background-image: linear-gradient(45deg, #F3F3F3 25%, transparent 5%), linear-gradient(-45deg, #F3F3F3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #F3F3F3 75%), linear-gradient(-45deg, transparent 75%, #F3F3F3 75%);
}

.row.buttons {
  display: flex;
  justify-content: flex-start;
}
</style>
